import {
  ComponentRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { BspkMenu, BspkTextField, MenuItem } from '@bspk/ngx-ui';

@Directive({
  selector: 'bspk-text-field[bspk-combobox]',
  standalone: true,
})
export class BspkCombobox<T = string> implements OnDestroy {
  private readonly overlay: Overlay = inject(Overlay);
  private readonly positionBuilder: OverlayPositionBuilder = inject(OverlayPositionBuilder);
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly host: BspkTextField = inject(BspkTextField);
  private overlayRef: OverlayRef | null = null;

  readonly items = input.required<MenuItem[]>();
  readonly valueSelected = output<T>();
  readonly hasFocus = signal(false);
  menuRef?: ComponentRef<BspkMenu<T>>;

  constructor() {
    effect(() => {
      if (this.items().length > 0 && this.hasFocus()) {
        this.openPanel();
        this.menuRef?.setInput('items', this.items());
      } else {
        this.closePanel();
      }
    });
    this.host.focusin.subscribe(() => {
      this.hasFocus.set(true);
    });
  }

  openPanel() {
    if (this.overlayRef?.hasAttached()) return;

    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPush(true)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    const portal = new ComponentPortal<BspkMenu<T>>(BspkMenu);
    this.menuRef = this.overlayRef.attach(portal);
    this.menuRef.location.nativeElement.style.width = `${
      this.elementRef.nativeElement.getBoundingClientRect().width
    }px`;
    this.menuRef.setInput('items', this.items());

    this.menuRef.instance.selectedValues.subscribe(([selected]) => {
      this.host.value.set(selected as string);
      this.valueSelected.emit(selected);
      this.closePanel();
      this.hasFocus.set(false);
    });

    this.overlayRef.backdropClick().subscribe(() => this.closePanel());
    this.overlayRef.detachments().subscribe(() => this.closePanel());
  }

  closePanel() {
    this.overlayRef?.detach();
    this.overlayRef = null;
  }

  ngOnDestroy() {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
